// src/services/webVitals.ts
// Web Vitals RUM sink'i — reportWebVitals'tan gelen metrikleri Firebase
// Analytics'e (logMyLiveEvent, guard'lı async) forward eder + dev console.
// Önceden reportWebVitals() argümansız çağrılıyordu → HİÇBİR ŞEY ölçülmüyordu
// ("körlemesine uçuş"). Artık saha verisi (CLS/INP/LCP/FCP/TTFB) toplanır.
//
// ── INP ATIFLARI (attribution) — 2026-08-19 ──────────────────────────────
// `reportWebVitals` artık `web-vitals/attribution` alt-yolunu kullanıyor
// (AYNI paket, yeni bağımlılık YOK). O yol INP metriğine bir `attribution`
// nesnesi ekler; burada GA4 alanlarına çevriliyor. Amaç, yol haritası
// §2.13'teki teşhis körlüğünü kapatmak:
//   "INP kötü"  →  "şu seçiciye dokununca, şu FAZDA, şu script blokluyor"
//
// INP'nin ÜÇ FAZI (toplamları ≈ INP değeri):
//   inputDelay          — dokunuştan ilk dinleyicinin ÇALIŞMAYA başlamasına
//                         kadar (main-thread meşgul → kod bölme/erteleme işi)
//   processingDuration  — dinleyicilerin çalıştığı süre (bizim JS'imiz)
//   presentationDelay   — dinleyiciler bittikten sonra BOYAMAYA kadar
//                         (layout/style/paint → CSS ve DOM işi)
// Hangi fazın suçlu olduğu, düzeltmenin NEREDE yapılacağını belirler; bu üç
// sayı olmadan "INP'yi iyileştirdik" iddiası ölçülemez.
//
// Privacy: yalnız metrik adı/değeri/rating + CSS SEÇİCİ ve script URL'i gider.
// PII yok — seçici DOM yapısıdır, kullanıcı içeriği değil. GA4 sınırı gereği
// metin alanları 100 karaktere kırpılır (KUYRUĞU tutulur: bilgi veren kısım
// yolun SONUDUR — dosya adı + fonksiyon).

import { logMyLiveEvent } from "../firebase";

/** GA4 string parametre tavanı. Aşan değer sessizce DÜŞÜRÜLÜR → kırpıyoruz. */
const GA4_METIN_SINIRI = 100;

/**
 * `web-vitals/attribution` INP atıf nesnesinin BİZE LAZIM OLAN yüzeyi.
 * Paketin kendi tipini `import type` ile çekmek `PerformanceLongAnimation
 * FrameTiming` gibi henüz lib.dom'da olmayan global tipleri bu dosyaya
 * taşırdı (TS sürümüne bağlı kırılganlık). Yapısal tip yeterli ve dayanıklı.
 */
export interface LoAFScript {
  duration?: number;
  sourceURL?: string;
  invoker?: string;
}

export interface LoAFGirdisi {
  duration?: number;
  scripts?: LoAFScript[];
}

export interface INPAtfi {
  interactionTarget?: string;
  interactionType?: string;
  inputDelay?: number;
  processingDuration?: number;
  presentationDelay?: number;
  longAnimationFrameEntries?: LoAFGirdisi[];
}

export interface WebVitalMetric {
  name: string; // "CLS" | "INP" | "LCP" | "FCP" | "TTFB"
  value: number;
  rating?: "good" | "needs-improvement" | "poor";
  id?: string;
  delta?: number;
  attribution?: INPAtfi;
}

// CLS ondalıklı (0..1), diğerleri ms → uygun yuvarlama.
export function roundMetric(name: string, value: number): number {
  if (name === "CLS") return Math.round(value * 1000) / 1000;
  return Math.round(value);
}

/** GA4 tavanına kırp — KUYRUĞU tut (dosya adı/fonksiyon yolun sonundadır). */
export function kirp(s: string, sinir: number = GA4_METIN_SINIRI): string {
  if (!s) return "";
  if (s.length <= sinir) return s;
  return `…${s.slice(s.length - (sinir - 1))}`;
}

/** ms alanlarını tam sayıya indir; sayı değilse alanı hiç gönderme. */
function msDeger(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;
}

/**
 * En UZUN LoAF girdisinin en UZUN script'ini seçer.
 * Neden "en uzun": LoAF dizisi bir etkileşimde birden çok kare taşıyabilir;
 * suçluyu arayan gözün ilk bakacağı yer en pahalı script'tir. Ortalama almak
 * tek bir 200 ms'lik tıkanmayı görünmez yapardı.
 */
export function enPahaliLoAFScript(
  girdiler?: LoAFGirdisi[],
): { url: string; invoker: string; sure: number } | null {
  if (!Array.isArray(girdiler) || girdiler.length === 0) return null;
  let enIyi: { url: string; invoker: string; sure: number } | null = null;
  for (const g of girdiler) {
    const scripts = g && Array.isArray(g.scripts) ? g.scripts : [];
    for (const s of scripts) {
      const sure = msDeger(s && s.duration);
      if (sure === null) continue;
      if (!enIyi || sure > enIyi.sure) {
        enIyi = { url: (s && s.sourceURL) || "", invoker: (s && s.invoker) || "", sure };
      }
    }
  }
  return enIyi;
}

/**
 * INP atıf nesnesini GA4 parametrelerine çevirir.
 * SAF fonksiyon (birim-testli): atıf yoksa BOŞ nesne döner — yani attribution
 * desteklemeyen tarayıcıda olayın ŞEKLİ değişmez, yalnız alanlar eksilir.
 */
export function inpAtifAlanlari(attribution?: INPAtfi): Record<string, unknown> {
  if (!attribution) return {};
  const out: Record<string, unknown> = {};
  if (attribution.interactionTarget) out.inp_target = kirp(attribution.interactionTarget);
  if (attribution.interactionType) out.inp_type = attribution.interactionType;

  const girdi = msDeger(attribution.inputDelay);
  const isleme = msDeger(attribution.processingDuration);
  const boyama = msDeger(attribution.presentationDelay);
  if (girdi !== null) out.inp_input_delay = girdi;
  if (isleme !== null) out.inp_processing = isleme;
  if (boyama !== null) out.inp_presentation = boyama;

  // Hangi faz baskın — tek bakışta okunacak sınıflandırma.
  if (girdi !== null && isleme !== null && boyama !== null) {
    const en = Math.max(girdi, isleme, boyama);
    out.inp_faz = en === girdi ? "input_delay" : en === isleme ? "processing" : "presentation";
  }

  const loaf = enPahaliLoAFScript(attribution.longAnimationFrameEntries);
  if (loaf) {
    out.inp_loaf_dur = loaf.sure;
    if (loaf.url) out.inp_loaf_script = kirp(loaf.url);
    if (loaf.invoker) out.inp_loaf_invoker = kirp(loaf.invoker);
  }
  const loafSayisi = Array.isArray(attribution.longAnimationFrameEntries)
    ? attribution.longAnimationFrameEntries.length
    : 0;
  if (loafSayisi) out.inp_loaf_count = loafSayisi;

  return out;
}

export function sendWebVital(metric: WebVitalMetric): void {
  if (!metric || !metric.name) return;
  const value = roundMetric(metric.name, metric.value);
  // Atıf YALNIZ INP için gönderilir: LCP/CLS atıfları ayrı alan setleri ister
  // ve GA4'ün olay-başına 25 parametre tavanını gereksiz yere yer.
  const atif = metric.name === "INP" ? inpAtifAlanlari(metric.attribution) : {};
  try {
    logMyLiveEvent("web_vital", {
      metric_name: metric.name,
      metric_value: value,
      metric_rating: metric.rating || "none",
      metric_id: metric.id || "",
      ...atif,
    });
  } catch (_) {
    /* analytics yoksa sessizce geç */
  }
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(`[web-vital] ${metric.name}=${value} (${metric.rating || "?"})`, atif);
  }
}
